import React from 'react';
import { Lock } from 'lucide-react';
import { SeverityMeter, SIGNAL_DISCLAIMER } from './SeverityMeter';
import { getPatternDefinition } from '../../../data/patternDefinitions';
import { formatSignalCount, isLockedValue, type NormalizedPattern, type ReportFinding, type Severity } from '../../../utils/scanReport';
interface DetailedFindingsProps {
  findings: ReportFinding[];
  patterns: NormalizedPattern[];
}
function toSeverity(value: string): Severity {
  const upper = value.toUpperCase();
  return upper === 'HIGH' || upper === 'LOW' ? upper : 'MEDIUM';
}
const CARD = 'rounded-xl border border-forensic-line bg-forensic-surface p-5 sm:p-6';

/** Full report: each pattern with the exact lines that triggered it. */
export function DetailedFindings({
  findings,
  patterns
}: DetailedFindingsProps) {
  if (findings.length === 0 && patterns.length === 0) {
    return <p className="rounded-xl border border-dashed border-forensic-line p-8 text-center text-sm text-forensic-muted" data-id="element-149" data-mp="3anddyc03g3xt">
        No patterns were detected, so there are no findings to break down.
      </p>;
  }
  if (findings.length > 0) {
    return <>
        <p className="mb-5 text-xs text-forensic-subtle" data-id="element-150" data-mp="9t5kbcjnaofl">{SIGNAL_DISCLAIMER}</p>
        <ul className="space-y-3" data-id="element-151" data-mp="3s6mx4lr8dfgx">
          {findings.map((finding, idx) => <li key={`${finding.pattern}-${idx}`} className={CARD} data-id="element-152" data-mp="2avq1ixfcqz" data-mpi={idx}>
              <FindingHeader title={finding.pattern} severity={toSeverity(finding.severity || 'MEDIUM')} data-id="element-153" data-mp="nr3m7igs4se7" />
              <p className="mt-2 text-sm leading-relaxed text-forensic-muted" data-id="element-154" data-mp="spexlxmq3ayj">
                {getPatternDefinition(finding.pattern)}
              </p>
              {isLockedValue(finding.example) ? <LockedEvidenceIndicator data-id="element-155" data-mp="jnfduxkbpsrw" /> : finding.example && <blockquote className="mt-4 whitespace-pre-line rounded-r-lg border-l-2 border-forensic-crimson/70 bg-white/[0.03] px-4 py-3 text-sm leading-relaxed text-gray-200" data-id="element-156" data-mp="2zf382l7yd770">
                    {finding.example}
                  </blockquote>}
            </li>)}
        </ul>
      </>;
  }
  return <>
      <p className="mb-5 text-xs text-forensic-subtle" data-id="element-157" data-mp="108o3vhzxmtjd">{SIGNAL_DISCLAIMER}</p>
      <ul className="space-y-3" data-id="element-158" data-mp="w845kb3mhjvn">
        {patterns.map((pattern, idx) => <li key={`${pattern.type}-${idx}`} className={CARD} data-id="element-159" data-mp="w61i9ytuu4um" data-mpi={idx}>
            <FindingHeader title={pattern.type} severity={pattern.severity} data-id="element-160" data-mp="rach5r26sfw7" />
            <p className="mt-2 text-sm leading-relaxed text-gray-300" data-id="element-161" data-mp="m9ex18xm9bs2">
              {pattern.description || getPatternDefinition(pattern.type)}
            </p>
            <p className="mt-3 text-xs text-forensic-subtle" data-id="element-162" data-mp="d9017fn10eab">
              {formatSignalCount(pattern.signalCount)}
            </p>
            {pattern.examples.length > 0 ? <ul className="mt-3 space-y-2" data-id="element-163" data-mp="2bmigtq3gdkuv">
                {pattern.examples.map((ex, i) => <li key={i} className="rounded-r-lg border-l-2 border-forensic-crimson/70 bg-white/[0.03] px-4 py-2.5 text-sm text-gray-200" data-id="element-164" data-mp="27kwwjjola4an" data-mpi={i}>
                    “{ex}”
                  </li>)}
              </ul> : pattern.evidenceLocked && <LockedEvidenceIndicator data-id="element-165" data-mp="76exymwxgwzl" />}
          </li>)}
      </ul>
    </>;
}
function LockedEvidenceIndicator() {
  return <p className="mt-4 inline-flex items-center gap-1.5 rounded-lg border border-forensic-line bg-forensic-card px-3 py-2 text-xs text-forensic-muted" data-id="element-166" data-mp="36ncxqvql9fz3">
      <Lock size={12} className="text-forensic-crimson-soft" aria-hidden="true" data-id="element-167" data-mp="3flyo3d2axzaf" />
      Evidence locked
    </p>;
}
function FindingHeader({
  title,
  severity
}: {
  title: string;
  severity: Severity;
}) {
  return <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4" data-id="element-168" data-mp="ux0tjepqwosp">
      <h3 className="text-base font-semibold text-white" data-id="element-169" data-mp="zuj4tbr8jg3v">{title}</h3>
      <SeverityMeter severity={severity} data-id="element-170" data-mp="ye5dymir0f1g" />
    </div>;
}